import type { Game, GameTilePosition, GameTrackingOrigin } from '@game/types'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, toValue, useGameOpenTracking, useUserInfo } from '#imports'

export interface UseGameListTrackingReturn {
    /** Bind to the GameList's `tracking-list-slug` prop so impressions share the slug. */
    trackingListSlug: ComputedRef<string>
    /** Bind to the GameList's `tracking-origin` prop so impressions share the origin. */
    trackingOrigin: GameTrackingOrigin
    onGameClick: (game: Game, position?: GameTilePosition) => void
}

/**
 * Clicks for a GameList, carrying the origin, slug, tile position and user its
 * impressions carry so the two streams join per surface. The one place the
 * joinable click payload is defined — a list that hand-rolls it instead will
 * silently miss the next field added here.
 *
 * `listSlug` accepts a getter for lists whose slug comes from a prop, so the
 * click slug cannot go stale while the impression slug follows the prop.
 */
export function useGameListTracking(trackingOrigin: GameTrackingOrigin, listSlug: MaybeRefOrGetter<string>): UseGameListTrackingReturn {
    const { data: userInfo } = useUserInfo()
    const { trackGameOpen } = useGameOpenTracking()

    const trackingListSlug = computed(() => toValue(listSlug))

    function onGameClick(game: Game, position?: GameTilePosition): void {
        // Spread, so a standalone tile with no grid position sends no position
        // keys at all rather than three undefined ones.
        trackGameOpen(trackingOrigin, game, {
            listSlug: trackingListSlug.value,
            ...position,
            userId: userInfo.value?.id,
        })
    }

    return { trackingListSlug, trackingOrigin, onGameClick }
}
